import { useMemo } from 'react';

interface HealthGaugeProps {
  label?: string;
  value: number; // 0-100
  size?: 'sm' | 'md' | 'lg';
  thresholds?: {
    warning: number;
    critical: number;
  };
  showValue?: boolean;
  subtitle?: string;
}

export default function HealthGauge({
  label,
  value,
  size = 'md',
  thresholds = { warning: 70, critical: 50 },
  showValue = true,
  subtitle,
}: HealthGaugeProps) {
  const dimensions = {
    sm: { size: 80, stroke: 6, fontSize: 'text-lg', labelSize: 'text-xs' },
    md: { size: 120, stroke: 8, fontSize: 'text-2xl', labelSize: 'text-sm' },
    lg: { size: 160, stroke: 10, fontSize: 'text-3xl', labelSize: 'text-base' },
  };

  const { size: diameter, stroke, fontSize, labelSize } = dimensions[size];
  const radius = (diameter - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedValue = Math.max(0, Math.min(100, value));
  const offset = circumference - (clampedValue / 100) * circumference;

  const color = useMemo(() => {
    if (clampedValue >= thresholds.warning) return '#059669'; // green
    if (clampedValue >= thresholds.critical) return '#d97706'; // yellow/amber
    return '#dc2626'; // red
  }, [clampedValue, thresholds]);

  // Background color for potential future use
  const _bgColor = useMemo(() => {
    if (clampedValue >= thresholds.warning) return '#dcfce7'; // green-100
    if (clampedValue >= thresholds.critical) return '#fef3c7'; // amber-100
    return '#fee2e2'; // red-100
  }, [clampedValue, thresholds]);
  void _bgColor; // Suppress unused variable warning

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: diameter, height: diameter }}>
        <svg
          className="transform -rotate-90"
          width={diameter}
          height={diameter}
        >
          {/* Background circle */}
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={stroke}
          />
          {/* Progress circle */}
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {showValue && (
            <span className={`font-bold ${fontSize}`} style={{ color }}>
              {Math.round(clampedValue)}%
            </span>
          )}
        </div>
      </div>
      {(label || subtitle) && (
        <div className="mt-2 text-center">
          {label && <span className={`font-medium text-gray-900 ${labelSize}`}>{label}</span>}
          {subtitle && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      )}
    </div>
  );
}
